import * as THREE from "three";
import { IBatchConfig, InstanceState } from "../types/core_types.js";
import { SceneManager } from "../../../core/scene_manager.js";

export abstract class InstanceBatch<TLayout extends Record<string, number> = any>
{
  protected instanced_mesh!: THREE.Mesh;
  protected geometry!: THREE.InstancedBufferGeometry;
  protected material!: THREE.Material;

  protected active_count: number = 0;
  protected pending_count: number = 0;
  protected last_update_time: number = 0;

  protected max_instances: number;
  protected layout?: TLayout;

  // DOD CPU metadata arrays
  protected start_times!: Float32Array;
  protected durations!: Float32Array;
  protected states!: Uint8Array;
  protected stop_times!: Float32Array;
  protected stop_durations!: Float32Array;
  protected group_ids!: Int32Array;

  protected current_group_id: number = 0;

  private next_stable_id: number = 1;
  protected idx_to_stable!: Int32Array;
  protected stable_to_idx: Map<number, number> = new Map();

  constructor( config: IBatchConfig<TLayout>, baseGeometry: THREE.BufferGeometry, material: THREE.Material )
  {
    this.max_instances = config.max_instances;
    this.layout = config.layout;

    // Create instanced geometry
    this.geometry = new THREE.InstancedBufferGeometry();
    ( this.geometry as THREE.BufferGeometry ).copy( baseGeometry );

    this.material = material;

    // Allocate DOD metadata arrays
    this.start_times = new Float32Array( this.max_instances );
    this.durations = new Float32Array( this.max_instances );
    this.states = new Uint8Array( this.max_instances );
    this.stop_times = new Float32Array( this.max_instances );
    this.stop_durations = new Float32Array( this.max_instances );
    this.group_ids = new Int32Array( this.max_instances );
    this.idx_to_stable = new Int32Array( this.max_instances ).fill( -1 );

    // Initialize layout attributes if layout is provided
    if ( this.layout )
    {
      for ( const [name, size] of Object.entries( this.layout ) )
      {
        const array = new Float32Array( this.max_instances * size );
        const attr = new THREE.InstancedBufferAttribute( array, size );
        attr.setUsage( THREE.DynamicDrawUsage );
        this.geometry.setAttribute( name, attr );
      }
    }

    // Create standard Mesh but with InstancedBufferGeometry
    this.instanced_mesh = new THREE.Mesh( this.geometry, this.material );
    this.instanced_mesh.frustumCulled = false;

    // Set count to 0 initially
    this.geometry.instanceCount = 0;

    // Add to three.js scene world
    SceneManager.world.add( this.instanced_mesh );
  }

  protected swapInstances( idx_a: number, idx_b: number ): void
  {
    if ( idx_a === idx_b ) return;

    // Swap GPU attributes
    if ( this.layout )
    {
      for ( const [name, size] of Object.entries( this.layout ) )
      {
        const attr = this.geometry.getAttribute( name ) as THREE.InstancedBufferAttribute;
        if ( attr )
        {
          const array = attr.array as Float32Array;
          const offset_a = idx_a * size;
          const offset_b = idx_b * size;
          for ( let f = 0; f < size; f++ )
          {
            const temp = array[offset_a + f];
            array[offset_a + f] = array[offset_b + f];
            array[offset_b + f] = temp;
          }
          attr.needsUpdate = true;
        }
      }
    }

    // Swap CPU metadata
    const temp_st = this.start_times[idx_a];
    this.start_times[idx_a] = this.start_times[idx_b];
    this.start_times[idx_b] = temp_st;

    const temp_dur = this.durations[idx_a];
    this.durations[idx_a] = this.durations[idx_b];
    this.durations[idx_b] = temp_dur;

    const temp_state = this.states[idx_a];
    this.states[idx_a] = this.states[idx_b];
    this.states[idx_b] = temp_state;

    const temp_stop = this.stop_times[idx_a];
    this.stop_times[idx_a] = this.stop_times[idx_b];
    this.stop_times[idx_b] = temp_stop;

    const temp_stop_dur = this.stop_durations[idx_a];
    this.stop_durations[idx_a] = this.stop_durations[idx_b];
    this.stop_durations[idx_b] = temp_stop_dur;

    const temp_group = this.group_ids[idx_a];
    this.group_ids[idx_a] = this.group_ids[idx_b];
    this.group_ids[idx_b] = temp_group;

    const stable_a = this.idx_to_stable[idx_a];
    const stable_b = this.idx_to_stable[idx_b];
    this.idx_to_stable[idx_a] = stable_b;
    this.idx_to_stable[idx_b] = stable_a;

    if ( stable_a !== -1 ) this.stable_to_idx.set( stable_a, idx_b );
    if ( stable_b !== -1 ) this.stable_to_idx.set( stable_b, idx_a );

    this.onSwapInstance( idx_a, idx_b );
  }

  protected onSwapInstance( idx_a: number, idx_b: number ): void {}

  protected allocateInstanceIndex( start_time: number, duration: number ): number
  {
    const total = this.active_count + this.pending_count;
    if ( total >= this.max_instances ) return -1;

    const is_pending = start_time > this.last_update_time;
    let idx = -1;

    if ( is_pending )
    {
      idx = total;
      this.pending_count++;
    }
    else
    {
      idx = this.active_count;
      if ( this.pending_count > 0 )
      {
        this.swapInstances( idx, total );
      }
      this.active_count++;
    }

    this.start_times[idx] = start_time;
    this.durations[idx] = duration;
    this.states[idx] = InstanceState.Alive;
    this.stop_times[idx] = 0;
    this.stop_durations[idx] = 0;
    this.group_ids[idx] = this.current_group_id;

    const stable_id = this.next_stable_id++;
    this.idx_to_stable[idx] = stable_id;
    this.stable_to_idx.set( stable_id, idx );

    return idx;
  }

  public getStableId( idx: number ): number
  {
    return this.idx_to_stable[idx];
  }

  public triggerDeathByGroup( group_id: number, current_time: number, fade_duration: number = 0 ): void
  {
    for ( let i = 0; i < this.active_count; i++ )
    {
      if ( this.group_ids[i] !== group_id ) continue;
      if ( this.states[i] !== InstanceState.Alive ) continue;

      this.states[i] = InstanceState.Stopping;
      this.stop_times[i] = current_time;
      this.stop_durations[i] = fade_duration;
    }
  }

  public triggerDeath( stable_id: number, current_time: number, fade_duration: number = 0 ): void
  {
    const idx = this.stable_to_idx.get( stable_id );
    if ( idx === undefined || idx === -1 ) return;
    if ( this.states[idx] !== InstanceState.Alive ) return;

    this.states[idx] = InstanceState.Stopping;
    this.stop_times[idx] = current_time;
    this.stop_durations[idx] = fade_duration;
  }

  public accelerateInstance( stable_id: number, factor: number, now: number ): void
  {
    const idx = this.stable_to_idx.get( stable_id );
    if ( idx === undefined || idx === -1 ) return;

    const elapsed = now - this.start_times[idx];
    const remaining = this.durations[idx] - elapsed;
    if ( remaining <= 0 ) return;

    this.start_times[idx] = now - elapsed * factor;
    this.durations[idx] = ( elapsed + remaining ) / factor;
  }

  public update( dt: number, now: number ): void
  {
    this.last_update_time = now;
    this.onUpdate( dt, now );

    // Process pending instances
    let p = 0;
    while ( p < this.pending_count )
    {
      const p_idx = this.active_count + p;
      const state = this.states[p_idx];
      let kill_pending = false;

      if ( state === InstanceState.Stopping )
      {
        const fade_duration = this.stop_durations[p_idx];
        const stop_time = this.stop_times[p_idx];
        if ( now > stop_time + fade_duration )
        {
          kill_pending = true;
        }
      }

      if ( kill_pending )
      {
        this.onInstanceDeath( p_idx );
        const stable_id = this.idx_to_stable[p_idx];
        if ( stable_id !== -1 )
        {
          this.stable_to_idx.delete( stable_id );
          this.idx_to_stable[p_idx] = -1;
        }
        const last_pending_idx = this.active_count + this.pending_count - 1;
        if ( p_idx !== last_pending_idx )
        {
          this.swapInstances( p_idx, last_pending_idx );
        }
        this.pending_count--;
      }
      else if ( now >= this.start_times[p_idx] )
      {
        if ( p_idx !== this.active_count )
        {
          this.swapInstances( p_idx, this.active_count );
        }
        this.active_count++;
        this.pending_count--;
      }
      else
      {
        p++;
      }
    }

    // Process active instances
    let i = 0;
    while ( i < this.active_count )
    {
      let is_alive = true;
      let progress = 0.0;

      const state = this.states[i];
      if ( state === InstanceState.Stopping )
      {
        const fade_duration = this.stop_durations[i];
        const stop_time = this.stop_times[i];
        if ( now > stop_time + fade_duration )
        {
          is_alive = false;
        }
        else
        {
          const fade_p = fade_duration > 0 ? Math.max( 0, Math.min( 1, ( now - stop_time ) / fade_duration ) ) : 1.0;
          const dur = this.durations[i];
          if ( dur > 0 )
          {
            const age = now - this.start_times[i];
            const natural_p = Math.min( 1.0, age / dur );
            progress = Math.max( natural_p, fade_p );
          }
          else
          {
            progress = fade_p;
          }
        }
      }
      else
      {
        const age = now - this.start_times[i];
        const dur = this.durations[i];
        if ( dur > 0 )
        {
          if ( age >= dur )
          {
            is_alive = false;
          }
          else
          {
            progress = age / dur;
          }
        }
      }

      if ( is_alive )
      {
        is_alive = this.updateInstance( progress, now, i );
      }

      if ( !is_alive )
      {
        this.onInstanceDeath( i );
        const stable_id = this.idx_to_stable[i];
        if ( stable_id !== -1 )
        {
          this.stable_to_idx.delete( stable_id );
          this.idx_to_stable[i] = -1;
        }
        const last_active_idx = this.active_count - 1;
        if ( i !== last_active_idx )
        {
          this.swapInstances( i, last_active_idx );
        }
        this.active_count--;

        if ( this.pending_count > 0 )
        {
          const hole_idx = this.active_count;
          const last_pending_idx = this.active_count + this.pending_count;
          this.swapInstances( hole_idx, last_pending_idx );
        }
      }
      else
      {
        i++;
      }
    }
  }

  public render(): void
  {
    this.geometry.instanceCount = this.active_count;
    if ( this.active_count === 0 && this.pending_count === 0 )
      return;
    if ( this.layout )
    {
      for ( const name of Object.keys( this.layout ) )
      {
        const attr = this.geometry.getAttribute( name );
        if ( attr )
        {
          attr.needsUpdate = true;
        }
      }
    }
    this.onRender();
  }

  protected abstract updateInstance( progress: number, now: number, idx: number ): boolean;
  protected onUpdate( dt: number, now: number ): void {}
  protected onInstanceDeath( idx: number ): void {}
  protected onRender(): void {}

  public getMesh(): THREE.Mesh
  {
    return this.instanced_mesh;
  }

  public killAll(): void
  {
    for ( let i = 0; i < this.active_count + this.pending_count; i++ )
    {
      this.onInstanceDeath( i );
      const stable_id = this.idx_to_stable[i];
      if ( stable_id !== -1 )
      {
        this.stable_to_idx.delete( stable_id );
      }
      this.idx_to_stable[i] = -1;
    }
    this.active_count = 0;
    this.pending_count = 0;
    this.geometry.instanceCount = 0;
  }

  public debugInstanceCount(): string
  {
    return `${this.active_count}a/${this.pending_count}p count=${this.geometry.instanceCount}`;
  }

  public debugUniformTextureSet(): boolean
  {
    const uniforms = ( this.material as THREE.ShaderMaterial ).uniforms;
    return !!uniforms?.uTexture?.value;
  }

  public hasInstances(): boolean
  {
    return this.active_count > 0 || this.pending_count > 0;
  }

  public get activeCount(): number { return this.active_count; }
  public get pendingCount(): number { return this.pending_count; }
  public get maxInstances(): number { return this.max_instances; }

  public getStats(): { active: number; pending: number; max: number }
  {
    return {
      active: this.active_count,
      pending: this.pending_count,
      max: this.max_instances,
    };
  }

  public dispose(): void
  {
    this.geometry.dispose();
    this.material.dispose();
  }
}
